import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Banner from "@/components/Banner";
import { Eyebrow, GoldButton } from "@/components/Reveal";
import { PHOTOS } from "@/data/content";

const VALUES = [
  {
    name: "Rigueur",
    desc: "Une méthodologie appliquée avec précision à chaque intervention, pour un niveau de propreté constant.",
  },
  {
    name: "Respect des espaces",
    desc: "Prendre soin de vos locaux, de vos mobiliers et de vos équipements comme s'il s'agissait des nôtres.",
  },
  {
    name: "Fiabilité",
    desc: "Le respect scrupuleux des horaires et des engagements d'intervention convenus.",
  },
  {
    name: "Communication",
    desc: "Des échanges transparents et une réactivité continue avec vos équipes et vos gestionnaires.",
  },
];

export default function About() {
  return (
    <>
      <Banner
        image={PHOTOS.aboutBanner}
        alt="Hall d'immeuble prestigieux entretenu par PUREVA"
        eyebrow="À propos"
        title={["PUREVA,", "le soin du détail."]}
        text="PUREVA est une entreprise de nettoyage au service des gestionnaires d'immeubles et des entreprises à Montréal et sur la Rive-Sud. Notre objectif est de contribuer à des espaces propres, accueillants et bien entretenus."
        testid="about-banner-title"
      />

      {/* ---------------- MISSION + VALEURS ---------------- */}
      <section className="bg-[#F4EFE6] py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <img
              src={PHOTOS.aboutStory}
              alt="Espace professionnel moderne et bien entretenu"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
            <span className="absolute left-0 top-0 h-14 w-px bg-gold" />
            <span className="absolute left-0 top-0 h-px w-14 bg-gold" />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <Eyebrow>Notre mission</Eyebrow>
            <h2
              data-testid="about-mission-text"
              className="mt-4 font-serif text-3xl leading-tight text-noir sm:text-4xl"
            >
              Un entretien adapté aux besoins de chaque lieu
            </h2>
            <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-neutral-600 sm:text-base">
              Chaque immeuble, chaque bureau et chaque espace commercial a sa
              propre configuration et ses propres exigences. Notre mission :
              proposer un entretien adapté aux besoins de chaque lieu, ni plus,
              ni moins.
            </p>
            <div data-testid="about-values-grid" className="mt-10 space-y-5">
              {VALUES.map((v, i) => (
                <motion.div
                  key={v.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: i * 0.07 }}
                  className="flex items-start gap-5 border-b border-gold/20 pb-5 last:border-0"
                >
                  <span className="font-serif text-xl italic text-gold">
                    0{i + 1}
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-noir">{v.name}</h3>
                    <p className="mt-1 text-[14px] leading-relaxed text-neutral-500">
                      {v.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------------- ÉQUIPE + CTA ---------------- */}
      <section className="bg-noir">
        <div className="grid lg:grid-cols-2">
          <div className="grain relative min-h-[260px] overflow-hidden lg:min-h-0">
            <img
              src={PHOTOS.aboutTeam}
              alt="Espace de travail professionnel bien entretenu"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-noir/30 to-noir/60" />
          </div>
          <div className="flex items-center px-6 py-20 sm:px-10 lg:px-16 lg:py-28">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9 }}
              className="max-w-xl"
            >
              <Eyebrow>Notre engagement</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory sm:text-4xl">
                Une équipe dédiée
              </h2>
              <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-mist sm:text-base">
                Des professionnels attentifs, respectueux de vos espaces et
                soucieux de la qualité de leur travail.
              </p>
              <div className="mt-9">
                <GoldButton as={Link} to="/contact" data-testid="about-cta-button">
                  Parlons de vos besoins
                  <ArrowRight className="h-4 w-4" />
                </GoldButton>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
